import type { MentionView } from '@leanin/shared';
import { mentionTokens } from '@leanin/shared';

// Remark plugin for the v2 post dialect: rewrites resolved `@handle` tokens
// in text nodes into member-profile link nodes (`/members/<userId>` with the
// display name as text). A mention of a member who is no longer active
// becomes an empty `span` marked `data-mention="deleted"` with no link —
// RichBodyRenderer fills in the muted "@Deleted user" label. Unresolved
// handles stay literal text. Link nodes are not descended into, so
// handle-shaped fragments inside URLs are left alone.
// Tokens come from the shared `mentionTokens` grammar, which is the same
// one the server resolver and RichBodyRenderer use — the three must agree
// about what a post says. It is lookbehind-free by construction
// (Safari < 16.4) and applies the preceding-character rule itself.

export const MEMBER_LINK_PREFIX = '/members/';
export const DELETED_MENTION_VALUE = 'deleted';

type MdastNode = {
  type: string;
  value?: string;
  url?: string;
  children?: MdastNode[];
  data?: {
    hName?: string;
    hProperties?: Record<string, string>;
  };
};

export function remarkPostMentions(mentions: ReadonlyArray<MentionView>) {
  const byHandle = new Map(mentions.map((m) => [m.handle, m]));
  return () => (tree: MdastNode) => {
    if (byHandle.size > 0) visit(tree, byHandle);
  };
}

function visit(node: MdastNode, byHandle: ReadonlyMap<string, MentionView>): void {
  const children = node.children;
  if (children === undefined) return;
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i]!;
    if (child.type === 'link') continue;
    if (child.type === 'text') {
      const replaced = splitMentions(child.value ?? '', byHandle);
      if (replaced !== null) children.splice(i, 1, ...replaced);
      continue;
    }
    visit(child, byHandle);
  }
}

function mentionNode(mention: MentionView): MdastNode {
  if (mention.kind === 'deleted') {
    return {
      type: 'deletedMention',
      children: [],
      data: { hName: 'span', hProperties: { dataMention: DELETED_MENTION_VALUE } },
    };
  }
  return {
    type: 'link',
    url: `${MEMBER_LINK_PREFIX}${mention.userId}`,
    children: [{ type: 'text', value: mention.name }],
  };
}

function splitMentions(
  text: string,
  byHandle: ReadonlyMap<string, MentionView>,
): MdastNode[] | null {
  if (!text.includes('@')) return null;
  const out: MdastNode[] = [];
  let last = 0;
  for (const token of mentionTokens(text)) {
    const mention = byHandle.get(token.handle);
    if (mention === undefined) continue;
    if (token.index > last) out.push({ type: 'text', value: text.slice(last, token.index) });
    out.push(mentionNode(mention));
    last = token.index + token.length;
  }
  if (out.length === 0) return null;
  if (last < text.length) out.push({ type: 'text', value: text.slice(last) });
  return out;
}
